import {
  GAME_LABELS,
  GAME_TYPES,
  STARTING_CHIPS,
  gameLabel,
  type CasinoMemberView,
  type GameType,
  type MemberStatsView,
} from '@tv-casino/shared';
import { formatSignedChips } from '../lib/format.js';
import type { CasinoLookup } from '@tv-casino/shared';

/**
 * The pure view-model behind the phone's casino lobby: balance card, personal
 * stats and the table lineup, derived from (casino lookup, member view) with
 * no React and no fetching. Everything the lobby prints is decided here.
 */

export interface LobbyStatLine {
  label: string;
  value: string;
}

export interface LobbyStatGroup {
  title: string;
  lines: LobbyStatLine[];
}

export interface LobbyTableRow {
  id: string;
  code: string;
  name: string;
  gameLabel: string;
  selected: boolean;
  live: boolean;
  closed: boolean;
  /** The one-line status the row prints next to its name. */
  note: string;
  /**
   * The table-picker action, or null for a closed table.
   */
  actionLabel: string | null;
}

export interface CasinoLobbyVM {
  /** True until the lookup lands; the lobby shows its loading line. */
  loading: boolean;
  casinoName: string;
  joinCode: string;
  /** The lobby screen showing now, and the strip that switches it. */
  tab: LobbyTab;
  tabs: LobbyTabDesc[];
  /** Balance as chips, or the starting stack for a phone that has never sat. */
  balanceLabel: string;
  balanceCaption: string;
  /** Signed table result: chips won or lost, house stakes excluded. */
  resultLabel: string;
  resultTone: 'win' | 'loss' | 'even';
  /** No membership row yet — this phone has scanned but never played. */
  newcomer: boolean;
  statGroups: LobbyStatGroup[];
  /** Shown instead of the groups when nothing has been played yet. */
  emptyStatsLine: string | null;
  tables: LobbyTableRow[];
  selectedTableName: string | null;
  /** True when the player has picked an open table. */
  canSit: boolean;
  sitLabel: string;
  /** Why sitting is off, when it is. */
  sitBlocked: string | null;
}

const chips = (n: number): string => n.toLocaleString();

const blackjackLines = (s: MemberStatsView): LobbyStatLine[] => [
  { label: 'Hands', value: chips(s.hands) },
  { label: 'Won', value: chips(s.wins) },
  { label: 'Lost', value: chips(s.losses) },
  { label: 'Pushed', value: chips(s.pushes) },
  { label: 'Blackjacks', value: chips(s.blackjacks) },
  { label: 'Best win', value: chips(s.biggestWin) },
  { label: 'Net', value: formatSignedChips(s.net) },
];

const holdemLines = (s: MemberStatsView): LobbyStatLine[] => [
  { label: 'Hands', value: chips(s.hands) },
  { label: 'Pots won', value: chips(s.potsWon) },
  { label: 'Showdowns', value: chips(s.showdowns) },
  { label: 'Biggest pot', value: chips(s.biggestPot) },
  { label: 'Net', value: formatSignedChips(s.net) },
];

const baccaratLines = (s: MemberStatsView): LobbyStatLine[] => [
  { label: 'Coups', value: chips(s.hands) },
  { label: 'Won', value: chips(s.wins) },
  { label: 'Lost', value: chips(s.losses) },
  { label: 'Pushed', value: chips(s.pushes) },
  { label: 'Best win', value: chips(s.biggestWin) },
  { label: 'Net', value: formatSignedChips(s.net) },
];

const rouletteLines = (s: MemberStatsView): LobbyStatLine[] => [
  { label: 'Spins', value: chips(s.hands) },
  { label: 'Won', value: chips(s.wins) },
  { label: 'Lost', value: chips(s.losses) },
  { label: 'Best win', value: chips(s.biggestWin) },
  { label: 'Net', value: formatSignedChips(s.net) },
];

const crapsLines = (s: MemberStatsView): LobbyStatLine[] => [
  { label: 'Hands', value: chips(s.hands) },
  { label: 'Won', value: chips(s.wins) },
  { label: 'Lost', value: chips(s.losses) },
  { label: 'Pushed', value: chips(s.pushes) },
  { label: 'Best win', value: chips(s.biggestWin) },
  { label: 'Net', value: formatSignedChips(s.net) },
];

const threecardLines = (s: MemberStatsView): LobbyStatLine[] => [
  { label: 'Hands', value: chips(s.hands) },
  { label: 'Won', value: chips(s.wins) },
  { label: 'Lost', value: chips(s.losses) },
  { label: 'Folds', value: chips(s.folds) },
  { label: 'Best win', value: chips(s.biggestWin) },
  { label: 'Net', value: formatSignedChips(s.net) },
];

const kenoLines = (s: MemberStatsView): LobbyStatLine[] => [
  { label: 'Draws', value: chips(s.hands) },
  { label: 'Tickets', value: chips(s.ticketsPlaced) },
  { label: 'Won', value: chips(s.wins) },
  { label: 'Lost', value: chips(s.losses) },
  { label: 'Best catch', value: chips(s.bestCatch) },
  { label: 'Best win', value: chips(s.biggestWin) },
  { label: 'Net', value: formatSignedChips(s.net) },
];

const GAME_STAT_LINES: Record<GameType, (s: MemberStatsView) => LobbyStatLine[]> = {
  blackjack: blackjackLines,
  holdem: holdemLines,
  baccarat: baccaratLines,
  roulette: rouletteLines,
  craps: crapsLines,
  threecard: threecardLines,
  keno: kenoLines,
};

/** The two lobby screens: pick a table, or read the night's numbers. */
export type LobbyTab = 'tables' | 'stats';

export interface LobbyTabDesc {
  id: LobbyTab;
  label: string;
  active: boolean;
}

const LOBBY_TABS = (tab: LobbyTab): LobbyTabDesc[] => [
  { id: 'tables', label: 'Tables', active: tab === 'tables' },
  { id: 'stats', label: 'Stats', active: tab === 'stats' },
];

export interface CasinoLobbyOptions {
  selectedTableCode?: string | null;
  alreadySeated?: boolean;
  /** Which lobby screen is showing; the table picker opens by default. */
  tab?: LobbyTab;
}

export function casinoLobbyModel(
  casino: CasinoLookup | null,
  me: CasinoMemberView | null,
  opts: CasinoLobbyOptions = {},
): CasinoLobbyVM {
  const { selectedTableCode = null, alreadySeated = false, tab = 'tables' } = opts;
  const view: LobbyTab = tab === 'stats' ? 'stats' : 'tables';
  const newcomer = me === null;
  const balance = me?.chips ?? STARTING_CHIPS;
  // The house's stakes are not winnings: the night's result is what the
  // tables moved, which is exactly what the stats counters add up to.
  const net = me ? me.chips - me.buyIn : 0;

  const groups: LobbyStatGroup[] = [];
  if (me) {
    for (const game of GAME_TYPES) {
      const stats = me.games[game];
      if (stats.hands > 0) {
        groups.push({ title: GAME_LABELS[game], lines: GAME_STAT_LINES[game](stats) });
      }
    }
  }

  const openTables = casino?.tables.filter((t) => t.status === 'open') ?? [];
  const selectedTable =
    openTables.find((t) => t.code === selectedTableCode) ?? null;
  const tables: LobbyTableRow[] = (casino?.tables ?? []).map((table) => {
    const selected = table.code === selectedTableCode;
    return {
      id: table.id,
      code: table.code,
      name: table.name,
      gameLabel: gameLabel(table.gameType),
      selected,
      live: table.live,
      closed: table.status === 'closed',
      note: table.status === 'closed' ? 'Closed' : table.live ? 'Playing' : 'Open',
      actionLabel: table.status === 'open' ? 'Sit here' : null,
    };
  });

  const sitBlocked = !casino
    ? null
    : alreadySeated
      ? 'You are already seated at another table.'
      : openTables.length === 0
      ? 'No tables are open yet — the owner opens the floor.'
      : !selectedTable
        ? 'Pick a table to sit down.'
          : null;

  return {
    loading: casino === null,
    casinoName: casino?.name ?? '',
    joinCode: casino?.joinCode ?? '',
    tab: view,
    tabs: LOBBY_TABS(view),
    balanceLabel: chips(balance),
    balanceCaption: newcomer ? 'Your starting stack' : 'Your chips',
    resultLabel: newcomer ? 'Nothing played yet' : `${formatSignedChips(net)} on the night`,
    resultTone: net > 0 ? 'win' : net < 0 ? 'loss' : 'even',
    newcomer,
    statGroups: groups,
    emptyStatsLine:
      groups.length === 0 ? 'No hands yet — sit down and the numbers start counting.' : null,
    tables,
    selectedTableName: selectedTable?.name ?? null,
    canSit: sitBlocked === null && casino !== null,
    sitLabel: selectedTable ? `Sit down at ${selectedTable.name}` : 'Sit down',
    sitBlocked,
  };
}
